import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_construction-hub-125/artifacts/hiq4o5va_Logo%20001%20-%20TR%20Kenscho%20Design.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t, getLocalizedLink, getPathWithoutLang } = useLanguage();

  const isActive = (path) => {
    const currentPath = getPathWithoutLang(location.pathname);
    return currentPath === path || (path === '/' && currentPath === '');
  };

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.construction'), path: '/services' },
    { name: t('nav.equipmentRental'), path: '/equipment-rental' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  const divisions = [
    { name: t('nav.workSite'), path: '/work-site' },
    { name: t('nav.transport'), path: '/transport' },
    { name: t('nav.concrete'), path: '/concrete' },
    { name: t('nav.excavation'), path: '/excavation' },
    { name: t('nav.welding'), path: '/welding' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav border-b border-border/40" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to={getLocalizedLink('/')} className="flex flex-col items-start" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Kenscho Design" 
              className="h-12 w-auto"
            />
            <span className="text-[10px] text-muted-foreground tracking-wide mt-0.5">RBQ 5829 4596 01</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={getLocalizedLink(link.path)}
                className={`nav-link text-sm font-medium tracking-wide uppercase ${
                  isActive(link.path) 
                    ? 'text-primary' 
                    : 'text-foreground/70 hover:text-foreground'
                }`}
                data-testid={`nav-${link.path === '/' ? 'home' : link.path.slice(1)}`}
              >
                {link.name}
              </Link>
            ))}

            {/* Divisions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="nav-link text-sm font-medium tracking-wide uppercase text-foreground/70 hover:text-foreground flex items-center gap-1"
                  data-testid="nav-divisions"
                >
                  {t('nav.divisions')} <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {divisions.map((division) => (
                  <DropdownMenuItem key={division.path} asChild>
                    <Link 
                      to={getLocalizedLink(division.path)}
                      className={isActive(division.path) ? 'text-primary' : ''}
                      data-testid={`nav-${division.path.slice(1)}`}
                    >
                      {division.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-border/60 hover:border-primary/50 transition-colors text-sm font-medium"
              data-testid="language-toggle"
            >
              <Globe className="h-4 w-4" />
              {language === 'en' ? 'FR' : 'EN'}
            </button>

            <Button asChild className="rounded-full px-6 btn-animate" data-testid="nav-quote-btn">
              <Link to={getLocalizedLink('/contact')}>{t('nav.getQuote')}</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-3">
            {/* Mobile Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-2 py-1 rounded-full border border-border/60 text-sm font-medium"
              data-testid="mobile-language-toggle"
            >
              <Globe className="h-4 w-4" />
              {language === 'en' ? 'FR' : 'EN'}
            </button>
            
            <button
              className="p-2"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pb-6 animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={getLocalizedLink(link.path)}
                  onClick={() => setIsOpen(false)}
                  className={`text-lg font-medium ${
                    isActive(link.path) ? 'text-primary' : 'text-foreground/70'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="border-t border-border pt-4 mt-2">
                <p className="text-sm text-muted-foreground uppercase tracking-wide mb-3">{t('nav.divisions')}</p>
                {divisions.map((division) => (
                  <Link
                    key={division.path}
                    to={getLocalizedLink(division.path)}
                    onClick={() => setIsOpen(false)}
                    className={`block py-2 text-lg font-medium ${
                      isActive(division.path) ? 'text-primary' : 'text-foreground/70'
                    }`}
                  >
                    {division.name}
                  </Link>
                ))}
              </div>
              <Button asChild className="rounded-full mt-4" onClick={() => setIsOpen(false)}>
                <Link to={getLocalizedLink('/contact')}>{t('nav.getQuote')}</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
